import postUserMilestones from "@faire/web-api/api/user/milestones/userToken/post";
import type { IUserMilestone } from "@faire/web-api/indigofair/data/IUserMilestone";
import { makeObservable } from "@faire/web/common/makeObservable";
import { singletonGetter } from "@faire/web/common/singletons/getter";
import differenceInDays from "date-fns/differenceInDays";
import forEach from "lodash/forEach";
import { observable } from "mobx";

import { buildIUserMilestone } from "@faire/retailer/lib/IUserMilestone";
import { getUserMilestones } from "@faire/retailer/serialized-data/getUserMilestones";

import { UserStore } from "./UserStore";

/**
 * @deprecated prefer to use `useMilestone` instead
 */
export class MilestonesStore {
  /**
   * @trackfunction
   */
  static get = singletonGetter(MilestonesStore);

  private userStore: UserStore = UserStore.get();

  @observable private userMilestones = observable.map<
    keyof typeof IUserMilestone.RetailerMilestone,
    IUserMilestone
  >();

  private listeners: Array<() => void> = [];

  constructor() {
    makeObservable(this);
    this.fetchMilestonesFromSerializedData();
  }

  private fetchMilestonesFromSerializedData = () => {
    forEach(getUserMilestones(), (userMilestone) => {
      this.userMilestones.set(userMilestone.retailer_milestone!, userMilestone);
    });
    this.listeners.forEach((callback) => callback());
  };

  record = (milestoneType: keyof typeof IUserMilestone.RetailerMilestone) => {
    if (!this.userStore.userToken) {
      return Promise.resolve();
    }
    return postUserMilestones(
      this.userStore.userToken,
      buildIUserMilestone({
        retailer_milestone: milestoneType,
      })
    ).then(this.addUserMilestone, () => {
      // do nothing...
    });
  };

  /**
   * Record the milestone only if it hasn't been recorded already.
   * This is the recommended record method if you don't care how many
   * times the milestone is recorded.
   */
  recordOnce = (
    milestoneType: keyof typeof IUserMilestone.RetailerMilestone
  ) => {
    if (!this.hasMilestoneOccurred(milestoneType)) {
      return this.record(milestoneType);
    }
    return Promise.resolve();
  };

  recordByDay = (
    milestoneType: keyof typeof IUserMilestone.RetailerMilestone
  ) => {
    if (!this.hasMilestoneOccurred(milestoneType)) {
      return this.record(milestoneType);
    }
    const updatedAt = this.get(milestoneType)?.updated_at ?? 0;
    const now = Date.now();
    if (differenceInDays(now, updatedAt) >= 1) {
      return this.record(milestoneType);
    }
    return Promise.resolve();
  };

  private addUserMilestone = (userMilestone: IUserMilestone) => {
    this.userMilestones.set(userMilestone.retailer_milestone!, userMilestone);
    this.listeners.forEach((callback) => callback());
  };

  hasMilestoneOccurred = (
    milestoneType: keyof typeof IUserMilestone.RetailerMilestone
  ): boolean => {
    return this.getMilestoneCount(milestoneType) > 0;
  };

  getMilestoneCount = (
    milestoneType: keyof typeof IUserMilestone.RetailerMilestone
  ): number => {
    return this.get(milestoneType)?.count || 0;
  };

  private get = (
    milestoneType: keyof typeof IUserMilestone.RetailerMilestone
  ) => {
    return this.userMilestones.get(milestoneType);
  };

  isMilestoneOverWeekOld = (
    milestoneType: keyof typeof IUserMilestone.RetailerMilestone
  ): boolean => {
    if (!this.hasMilestoneOccurred(milestoneType)) {
      return false;
    }
    const updatedAt = this.get(milestoneType)?.updated_at ?? 0;
    const now = Date.now();
    if (differenceInDays(now, updatedAt) >= 7) {
      return true;
    }
    return false;
  };

  isMilestoneOverDaysOld = (
    milestoneType: keyof typeof IUserMilestone.RetailerMilestone,
    days: number
  ): boolean => {
    if (!this.hasMilestoneOccurred(milestoneType)) {
      return false;
    }
    const updatedAt = this.get(milestoneType)?.updated_at ?? 0;
    const now = Date.now();
    if (differenceInDays(now, updatedAt) >= days) {
      return true;
    }
    return false;
  };
}
